import type { Task } from '@paintscout/api'
import { uuid } from '@paintscout/util/builder'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'

// Handle use/updating of task values for TaskForm
export const useTaskValues = ({
  task,
  defaultEstimator
}: {
  task: Partial<Task>
  defaultEstimator?: string
}): {
  taskValues: Task
  setTaskValues: (task: Task) => void
  handleFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleNotesChange: (notes: string) => void
  handleOwnerChange: (option: DropdownSelectOption) => void
  handleDealChange: (dealOption: DropdownSelectOption) => void
  handleDateChange: (date: Date) => void
  handleTimeChange: (time: Date) => void
} => {
  const [values, setValues] = useState<Task>(getValues({ task, defaultEstimator }))

  const handleFieldChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { id, value } = event.target
      setValues((prev) => ({ ...prev, [id]: value }))
    },
    [values]
  )

  const handleNotesChange = useCallback(
    (notes: string) => {
      setValues((prev) => ({
        ...prev,
        notes
      }))
    },
    [values]
  )

  const handleDealChange = useCallback(
    (option: DropdownSelectOption) => {
      setValues((prev) => ({
        ...prev,
        dealId: option.value,
        dealName: option.label
      }))
    },
    [values]
  )

  const handleDateChange = useCallback(
    (date: Date) => {
      if (!date) {
        // Clearing the date also clears the time
        setValues((prev) => ({ ...prev, startDateTime: null }))
        return
      }

      // Use the date from the new value, but the hours/minutes from current startDateTime
      const currentDate = values.startDateTime ? new Date(values.startDateTime) : new Date()
      const updatedDate = new Date(date)
      updatedDate.setHours(currentDate.getHours())
      updatedDate.setMinutes(currentDate.getMinutes())
      updatedDate.setSeconds(0)
      updatedDate.setMilliseconds(0)

      setValues((prev) => ({
        ...prev,
        startDateTime: updatedDate.valueOf()
      }))
    },
    [values]
  )

  const handleTimeChange = useCallback(
    (time: Date) => {
      // Just want to update the hours:minutes of hte startDateTime with our new given value
      setValues((prev) => ({
        ...prev,
        startDateTime: time.valueOf()
      }))
    },
    [values]
  )

  const handleOwnerChange = useCallback(
    (option: DropdownSelectOption) => {
      setValues((prev) => ({
        ...prev,
        owner: option?.value
          ? {
              id: option.value,
              firstName: option.label.split(' ')?.[0],
              lastName: option.label.split(' ')?.[1] ?? ''
            }
          : null
      }))
    },
    [values]
  )

  useEffect(() => {
    setValues(getValues({ task, defaultEstimator }))
  }, [task, defaultEstimator])

  return {
    taskValues: values,
    setTaskValues: setValues,
    handleDateChange,
    handleTimeChange,
    handleDealChange,
    handleFieldChange,
    handleOwnerChange,
    handleNotesChange
  }
}

function getValues(args: { task?: Partial<Task>; defaultEstimator?: string }): Task {
  const { task = {}, defaultEstimator } = args
  const sanitized = omitBy(task, isNull)

  return {
    _id: uuid(),
    _rev: null,
    _deleted: null,
    dealId: null,
    title: '',
    owner: defaultEstimator ? { id: defaultEstimator } : null,
    startDateTime: null,
    created: Date.now(),
    updated: null,
    completed: false,
    completedDate: null,
    notes: '',
    ...sanitized,
    type: 'task'
  }
}
